// src/hooks/useAIFeedback.js - Improved with proper error handling and I Ching hexagram integration
import { useState, useCallback } from 'react';
import { useProfile } from '../../features/profile/context/ProfileContext';
import { NetworkError, ValidationError } from '../../types';
import { trackAIFeedbackRequest } from '../utils/analytics/analytics';
import { getUserPatterns } from '../../features/insights/services/patternDetectionService';
import { getUserMemory, getStatistics } from '../../features/insights/services/aiMemoryService';
import { findSimilarEntries, formatSimilarEntriesForContext } from '../../features/insights/services/semanticSearchService';
import logger from '../utils/core/logger';


export function useAIFeedback() {
  const [state, setState] = useState({
    feedback: null,
    loading: false,
    error: null
  });

  const { profile, hexagramProfile, getAIContext } = useProfile();

  const requestFeedback = useCallback(async (entryText, additionalContext = '') => {
    if (!entryText?.trim()) {
      throw new ValidationError('Entry text is required');
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Build personalized context
      let context = additionalContext;
      
      if (profile?.birthDate) {
        // Add basic profile context
        const birthDate = new Date(profile.birthDate);
        const age = new Date().getFullYear() - birthDate.getFullYear();
        
        let profileContext = `User is ${age} years old`;
        
        // Add birth location context
        if (profile.birthPlace) {
          profileContext += ` and was born in ${profile.birthPlace}`;
        }
        
        // Add zodiac context (simple version)
        const zodiacSigns = ['Capricorn', 'Aquarius', 'Pisces', 'Aries', 'Taurus', 'Gemini', 
                           'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius'];
        const month = birthDate.getMonth();
        const day = birthDate.getDate();
        
        // Simple zodiac calculation
        let zodiacIndex;
        if (month === 0 && day <= 19 || month === 11 && day >= 22) zodiacIndex = 0; // Capricorn
        else if (month === 0 || month === 1 && day <= 18) zodiacIndex = 1; // Aquarius
        else if (month === 1 || month === 2 && day <= 20) zodiacIndex = 2; // Pisces
        else if (month === 2 || month === 3 && day <= 19) zodiacIndex = 3; // Aries
        else if (month === 3 || month === 4 && day <= 20) zodiacIndex = 4; // Taurus
        else if (month === 4 || month === 5 && day <= 20) zodiacIndex = 5; // Gemini
        else if (month === 5 || month === 6 && day <= 22) zodiacIndex = 6; // Cancer
        else if (month === 6 || month === 7 && day <= 22) zodiacIndex = 7; // Leo
        else if (month === 7 || month === 8 && day <= 22) zodiacIndex = 8; // Virgo
        else if (month === 8 || month === 9 && day <= 22) zodiacIndex = 9; // Libra
        else if (month === 9 || month === 10 && day <= 21) zodiacIndex = 10; // Scorpio
        else zodiacIndex = 11; // Sagittarius
        
        profileContext += ` (${zodiacSigns[zodiacIndex]})`;

        profileContext += '. Please provide personalized insights that consider their age, background, and astrological profile when relevant to their journal entry.';

        // Add I Ching hexagram context for invisible personalization
        if (hexagramProfile && getAIContext) {
          try {
            const aiContext = getAIContext(entryText);

            if (aiContext && aiContext.hexagram) {
              const hex = aiContext.hexagram;

              // Build subtle guidance context for AI
              let hexagramContext = `\n\nPersonalization guidance (subtle, not explicit): `;
              hexagramContext += `User's core energy relates to "${hex.name}" themes: ${hex.themes.join(', ')}. `;

              // Determine which aspect (shadow/gift/essence) to emphasize
              if (aiContext.matchingKeywords && aiContext.matchingKeywords.length > 0) {
                // Keywords detected - likely working through shadow
                hexagramContext += `Current keywords suggest shadow work around: ${hex.shadow}. `;
                hexagramContext += `AI guidance: ${hex.ai_guidance.shadow_reframe} `;
              } else {
                // No specific keywords - celebrate gift
                hexagramContext += `Celebrate their natural gift: ${hex.gift}. `;
                hexagramContext += `AI guidance: ${hex.ai_guidance.gift_celebration} `;
              }

              // Add communication notes for tone
              hexagramContext += `Communication style: ${hex.ai_guidance.communication_notes}`;

              // Important: Make AI use this subtly, not explicitly mention hexagrams
              hexagramContext += ` (IMPORTANT: Use this context to inform tone and insights, but do NOT explicitly mention hexagrams, I Ching, or this framework to the user.)`;

              profileContext += hexagramContext;

              if (import.meta.env.DEV) {
                logger.debug('🔮 Added hexagram context for:', hex.name);
              }
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              logger.debug('Hexagram context not available:', error);
            }
          }
        }

        context = `${profileContext} ${context}`.trim();
      }

      // Use environment variable or fallback to local emulator
      const aiProxyUrl = import.meta.env.VITE_AI_PROXY_URL ||
                        (import.meta.env.DEV
                          ? 'http://localhost:5001/clarity-app-334ee/us-central1/aiFeedback'
                          : '/api/aiFeedback');
      
      if (import.meta.env.DEV) {
        logger.debug('🤖 Generating AI feedback using:', aiProxyUrl.includes('localhost') ? 'local emulator' : 'production');
      }

      // Retry logic for connection issues
      let response;
      let lastError;
      
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          if (attempt > 1 && import.meta.env.DEV) {
            logger.debug(`🔄 Retry attempt ${attempt}...`);
          }
          
          // Get authentication token
          const { auth } = await import('../utils/core/firebase-config');
          const user = auth.currentUser;
          if (!user) {
            throw new Error('Authentication required. Please log in and try again.');
          }

          const token = await user.getIdToken();

          response = await fetch(aiProxyUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Origin': window.location.origin
            },
            body: JSON.stringify({
              entryText: entryText.trim(),
              context: context.trim()
            }),
          });
          
          break; // Success, exit retry loop
          
        } catch (error) {
          if (import.meta.env.DEV) {
            logger.warn(`Connection attempt ${attempt} failed:`, error.message);
          }
          lastError = error;
          
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      if (!response) {
        throw new Error(`Failed to connect after 3 attempts. Last error: ${lastError?.message || 'Unknown error'}`);
      }

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          logger.error('AI service error:', errorData);
          errorMessage = errorData.error || `Server error: ${response.status}`;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        throw new NetworkError(errorMessage, response.status);
      }

      const data = await response.json();
      const feedback = data.feedback?.trim();
      
      if (!feedback) {
        logger.error('No feedback in AI response:', data);
        throw new Error('No feedback received from AI service');
      }

      logger.debug('✅ AI feedback generated successfully');
      trackAIFeedbackRequest(true);
      setState({ feedback, loading: false, error: null });
      return feedback;

    } catch (error) {
      logger.error('AI feedback request failed:', error);
      
      let userError;
      if (error instanceof NetworkError) {
        if (error.status === 429) {
          userError = 'Too many requests. Please wait a moment and try again.';
        } else if (error.status >= 500) {
          userError = 'AI service is temporarily unavailable. Please try again later.';
        } else {
          userError = error.message;
        }
      } else if (error instanceof ValidationError) {
        userError = error.message;
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userError = 'Unable to connect. Please check your internet connection.';
      } else {
        userError = 'An unexpected error occurred. Please try again.';
      }

      trackAIFeedbackRequest(false);
      setState({ feedback: null, loading: false, error: userError });
      throw new Error(userError);
    }
  }, [profile, hexagramProfile, getAIContext]);

  const clearFeedback = useCallback(() => {
    setState({ feedback: null, loading: false, error: null });
  }, []);

  // NEW: Generate deep personalized analysis
  const generateAnalysis = useCallback(async (entryContent) => {
    if (!entryContent?.text?.trim()) {
      throw new ValidationError('Entry text is required for analysis');
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Get authentication token
      const { auth } = await import('../utils/core/firebase-config');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Authentication required');
      }
      const token = await user.getIdToken();

      // Load user statistics, patterns, and memory for historical context
      const userStats = user?.uid ? await getStatistics(user.uid) : null;
      const userPatterns = user?.uid ? await getUserPatterns(user.uid) : null;
      const userMemory = user?.uid ? await getUserMemory(user.uid) : null;

      // Find similar past entries (Phase 4.2)
      const similarEntries = user?.uid && entryContent?.text
        ? await findSimilarEntries(user.uid, entryContent.text, token, 3)
        : [];

      // Build analysis context from SAVED profile data + user statistics + patterns + memory + similar entries
      const context = buildAnalysisContext(entryContent, profile, hexagramProfile, userStats, userPatterns, userMemory, similarEntries);

      // Use environment variable or fallback
      const aiProxyUrl = import.meta.env.VITE_AI_PROXY_URL ||
                        (import.meta.env.DEV
                          ? 'http://localhost:5001/clarity-app-334ee/us-central1/aiFeedback'
                          : '/api/aiFeedback');

      if (import.meta.env.DEV) {
        logger.debug('🔍 Generating AI analysis...');
      }

      const response = await fetch(aiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          entryText: entryContent.text.trim(),
          context: context,
          responseType: 'analysis', // Signal to backend this is analysis
          requestJson: true // Request JSON format
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new NetworkError(errorData.error || `Server error: ${response.status}`, response.status);
      }

      const data = await response.json();
      const analysis = parseAnalysisResponse(data);

      logger.debug('✅ AI analysis generated successfully');
      trackAIFeedbackRequest(true);
      setState(prev => ({ ...prev, loading: false, error: null }));
      return analysis;

    } catch (error) {
      logger.error('AI analysis failed:', error);
      trackAIFeedbackRequest(false);
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [profile, hexagramProfile]);

  // NEW: Generate writing prompts
  const generatePrompts = useCallback(async (recentEntries = [], options = {}) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Extract recent themes
      const recentThemes = extractRecentThemes(recentEntries);

      // Build prompts context from SAVED profile data
      const context = buildPromptsContext(profile, hexagramProfile, recentThemes, options);

      // Get authentication token
      const { auth } = await import('../utils/core/firebase-config');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Authentication required');
      }
      const token = await user.getIdToken();

      // Use environment variable or fallback
      const aiProxyUrl = import.meta.env.VITE_AI_PROXY_URL ||
                        (import.meta.env.DEV
                          ? 'http://localhost:5001/clarity-app-334ee/us-central1/aiFeedback'
                          : '/api/aiFeedback');

      if (import.meta.env.DEV) {
        logger.debug('💡 Generating AI prompts...');
      }

      const response = await fetch(aiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          entryText: '', // Not needed for prompts
          context: context,
          responseType: 'prompts', // Signal to backend this is prompts
          requestJson: true // Request JSON format
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new NetworkError(errorData.error || `Server error: ${response.status}`, response.status);
      }

      const data = await response.json();
      const prompts = parsePromptsResponse(data);

      logger.debug('✅ AI prompts generated successfully');
      trackAIFeedbackRequest(true);
      setState(prev => ({ ...prev, loading: false, error: null }));
      return prompts;

    } catch (error) {
      logger.error('AI prompts generation failed:', error);
      trackAIFeedbackRequest(false);
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [profile, hexagramProfile]);

  // NEW: Smart text formatting (AI-powered)
  const formatText = useCallback(async (text, options = {}) => {
    if (!text || text.length < 10) {
      return { formatted: text, changes: [] };
    }

    const {
      fixGrammar = true,
      improveParagraphs = true,
      formatDates = true,
      formatLists = true,
      preserveTone = true
    } = options;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const context = `You are helping format a journal entry for clarity and readability.

Your task:
${fixGrammar ? '- Fix grammar and spelling errors' : ''}
${improveParagraphs ? '- Improve paragraph structure (proper spacing, flow)' : ''}
${formatDates ? '- Format dates consistently (e.g., "January 16, 2025")' : ''}
${formatLists ? '- Format lists with proper markdown bullets or numbers' : ''}
${preserveTone ? '- CRITICAL: Preserve the author\'s voice, tone, and personal style - only fix obvious errors' : ''}

Original entry:
${text}

Return ONLY the formatted text with no explanations, commentary, or markdown code blocks:`;

      const formatted = await requestFeedback(context, 'Text formatting');

      const changes = [];
      if (formatted !== text) {
        if (fixGrammar) changes.push('grammar');
        if (improveParagraphs) changes.push('paragraphs');
        if (formatDates) changes.push('dates');
        if (formatLists) changes.push('lists');
      }

      setState(prev => ({ ...prev, loading: false }));

      return {
        formatted: formatted || text,
        changes,
        hasChanges: formatted !== text,
        original: text
      };

    } catch (error) {
      logger.error('AI formatting failed:', error);
      setState(prev => ({ ...prev, loading: false }));

      // Return original text on failure
      return {
        formatted: text,
        changes: [],
        hasChanges: false,
        original: text,
        error: error.message
      };
    }
  }, [requestFeedback]);

  /**
   * Detect mood from journal entry text
   */
  const detectMood = useCallback(async (entryText) => {
    if (!entryText?.trim() || entryText.trim().length < 10) {
      return { primaryMood: null, confidence: 0 };
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { auth } = await import('../utils/core/firebase-config');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Authentication required');
      }

      const token = await user.getIdToken();

      const aiProxyUrl = import.meta.env.VITE_AI_PROXY_URL ||
        '/api/aiFeedback';

      // 8-second timeout — mood detection runs in background while modal is already visible,
      // so this is just a safety net to prevent hanging indefinitely
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(aiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          entryText: entryText.trim(),
          responseType: 'mood_detection'
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Mood detection failed: ${response.statusText}`);
      }

      const data = await response.json();
      const moodData = JSON.parse(data.feedback);

      setState(prev => ({ ...prev, loading: false }));

      return {
        primaryMood: moodData.primaryMood,
        secondaryMood: moodData.secondaryMood || null,
        confidence: moodData.confidence,
        reasoning: moodData.reasoning
      };

    } catch (error) {
      if (error.name === 'AbortError') {
        // Timeout is expected — mood detection runs in background and user can pick manually
        logger.debug?.('Mood detection timed out, falling back to manual selection');
      } else {
        logger.error('Mood detection failed:', error);
      }
      setState(prev => ({ ...prev, loading: false }));
      return { primaryMood: null, confidence: 0 };
    }
  }, []);

  return {
    ...state,
    requestFeedback,
    clearFeedback,
    generateAnalysis, // Deep personalized analysis
    generatePrompts,  // Writing prompts
    formatText,       // Smart text formatting
    detectMood,       // NEW: AI mood detection
    isLoading: state.loading,
    hasError: !!state.error
  };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// HELPER FUNCTIONS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Build context for ANALYSIS (deep personalized insights)
function buildAnalysisContext(entryContent, profile, hexagramProfile, userStats = null, userPatterns = null, userMemory = null, similarEntries = []) {
  const now = new Date();
  const hour = now.getHours();
  const timeOfDay = getTimeOfDay(hour);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // HANDWRITING ANALYSIS CONTEXT
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let handwritingContext = '';
  const handwriting = entryContent.handwritingAnalysis;

  if (handwriting) {
    const parts = [];

    // Writing style insights
    if (handwriting.writingStyle) {
      const style = handwriting.writingStyle;
      if (style.pace && style.pace !== 'balanced') {
        parts.push(`Writing pace: ${style.pace}`);
      }
      if (style.energy && style.energy !== 'neutral') {
        parts.push(`Energy level: ${style.energy}`);
      }
      if (style.consistency && style.consistency !== 'steady') {
        parts.push(`Consistency: ${style.consistency}`);
      }
    }

    // Emphasis markers
    if (handwriting.emphasisMarkers?.totalEmphasis > 0) {
      const markers = handwriting.emphasisMarkers;
      const emphasisDetails = [];
      if (markers.underlinedPhrases > 0) emphasisDetails.push(`${markers.underlinedPhrases} underlined`);
      if (markers.allCapsWords > 0) emphasisDetails.push(`${markers.allCapsWords} emphasized words`);
      if (markers.stars > 0) emphasisDetails.push(`${markers.stars} starred points`);
      if (markers.exclamationClusters > 0) emphasisDetails.push(`${markers.exclamationClusters} exclamations`);
      parts.push(`Visual emphasis: ${emphasisDetails.join(', ')}`);
    }

    // Headers detected
    if (handwriting.detectedHeaders?.length > 0) {
      parts.push(`Organized with ${handwriting.detectedHeaders.length} section headers`);
    }

    // Non-text regions (sketches/doodles)
    if (handwriting.nonTextRegions?.length > 0) {
      parts.push(`Contains ${handwriting.nonTextRegions.length} visual elements (sketches/doodles)`);
    }

    // Writing insight
    if (handwriting.writingInsight) {
      parts.push(`Handwriting observation: "${handwriting.writingInsight}"`);
    }

    if (parts.length > 0) {
      handwritingContext = `\n\nHANDWRITING ANALYSIS (from physical journal scan - use subtly):
- ${parts.join('\n- ')}
- This entry was handwritten, suggesting intentional, reflective journaling`;
    }
  }

  // Multi-page context
  if (entryContent.pageCount > 1) {
    handwritingContext += `\n- This is a ${entryContent.pageCount}-page handwritten entry, showing depth of reflection`;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // GRACEFUL PROFILE HANDLING
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let age = null;
  let zodiacSign = null;
  let hasProfileData = false;

  if (profile && profile.birthDate) {
    try {
      const birthDate = new Date(profile.birthDate);

      // Validate date is valid
      if (!isNaN(birthDate.getTime())) {
        age = new Date().getFullYear() - birthDate.getFullYear();
        zodiacSign = calculateZodiacSign(birthDate);
        hasProfileData = true;
      }
    } catch (error) {
      logger.warn('[buildAnalysisContext] Failed to calculate age/zodiac:', error);
      // Continue without profile data
    }
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RICH ASTROLOGICAL CONTEXT (if available)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let hexagramContext = '';
  if (hexagramProfile) {
    const personality = hexagramProfile.personalityDetails || hexagramProfile.personality;
    const westernAstro = hexagramProfile.westernAstrology;
    const humanDesign = hexagramProfile.humanDesign;
    const natalChart = hexagramProfile.natalChart;

    // Build multi-layered context
    let astroContext = [];

    // Western Astrology (Co-Star style)
    if (westernAstro) {
      astroContext.push(`Sun in ${westernAstro.sun || zodiacSign} (core identity, ego)`);
      if (westernAstro.moon && westernAstro.moon !== 'Unknown') {
        astroContext.push(`Moon in ${westernAstro.moon} (emotional nature, inner world)`);
      }
      if (westernAstro.rising && westernAstro.rising !== 'Unknown') {
        astroContext.push(`${westernAstro.rising} Rising (outer personality, first impressions)`);
      }
    }

    // Human Design (MyBodyGraph style)
    let hdContext = '';
    if (humanDesign && humanDesign.type) {
      hdContext = `
- Energy Type: ${humanDesign.type}
- Life Strategy: ${humanDesign.strategy}
- Decision-Making: ${humanDesign.authority} authority
- Life Theme: ${humanDesign.profile} profile (${humanDesign.profileMeaning?.split(' -')[0] || ''})`;
    }

    // Hexagram/I Ching wisdom
    let hexContext = '';
    if (personality) {
      hexContext = `
- Core Energy: ${personality.name || 'Authentic expression'}
- Natural Gifts: ${personality.gift || 'Unique perspective'}
- Growth Edge: ${personality.shadow || 'Self-awareness'}`;
    }

    // Combine all layers
    hexagramContext = `

PERSONAL ENERGY PROFILE (weave subtly into analysis, don't explicitly mention sources):
${astroContext.length > 0 ? '- Astrological Blueprint: ' + astroContext.join('; ') : ''}${hdContext}${hexContext}

GUIDANCE FOR AI:
- Use this profile to understand their natural patterns and tendencies
- Reference their energy type and strategy in how you frame suggestions
- Connect journal themes to their emotional nature (Moon) and decision-making style
- Make it feel like you deeply "get" them, not like you're reading their chart`;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // USER HISTORY & PATTERNS (Personal AI Memory System)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let historicalContext = '';

  if (userStats && userStats.totalEntries > 0) {
    const relationshipDepth = userStats.totalEntries < 5 ? 'early' :
                              userStats.totalEntries < 30 ? 'established' : 'deep';

    historicalContext = `

YOUR RELATIONSHIP WITH THIS USER (use this to inform tone and depth):
- You've analyzed ${userStats.totalEntries} entries from this user
- Current writing streak: ${userStats.currentStreak} days (longest: ${userStats.longestStreak} days)
- Average entry length: ${userStats.averageEntryLength} words
- Writes most often during: ${userStats.preferredWritingTime || 'various times'}
${userStats.mostProductiveDays?.length > 0 ? `- Most active on: ${userStats.mostProductiveDays.join(', ')}` : ''}

EMOTIONAL PATTERNS YOU'VE OBSERVED:
- Dominant mood: ${userStats.dominantMood || 'Not yet established'}
- Mood trend (last 30 days): ${userStats.moodTrend || 'stable'}
- Sentiment trajectory:
  - Last 7 days: ${userStats.sentimentTrajectory?.last7Days >= 0 ? 'positive' : 'challenging'} (${(userStats.sentimentTrajectory?.last7Days || 0).toFixed(2)})
  - Last 30 days: ${userStats.sentimentTrajectory?.last30Days >= 0 ? 'positive' : 'challenging'} (${(userStats.sentimentTrajectory?.last30Days || 0).toFixed(2)})
${userStats.sentimentTrajectory?.last30Days > userStats.sentimentTrajectory?.last7Days ? '- Recent dip in sentiment - be extra supportive' : ''}

HOW TO USE THIS CONTEXT:
${relationshipDepth === 'early'
  ? '- You are still getting to know this user. Focus on supportive, exploratory feedback. Ask questions to understand them better.'
  : relationshipDepth === 'established'
  ? '- You have a good understanding of this user. Reference patterns you\'ve noticed and offer personalized suggestions based on their history.'
  : '- You deeply know this user\'s journey. Acknowledge their growth, reference their transformation, and offer nuanced insights that show you remember their patterns and progress.'
}
- If current mood differs from their dominant mood, acknowledge this shift
- If sentiment is declining, offer gentle support and remind them of their resilience (reference streak if applicable)
- Make it feel like you remember them, not like you're analyzing data`;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PATTERN DETECTION CONTEXT (Phase 2)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let patternContext = '';

  if (userPatterns && userPatterns.recurringThemes?.length > 0) {
    const topThemes = userPatterns.recurringThemes
      .sort((a, b) => b.mentionCount - a.mentionCount)
      .slice(0, 5);

    const topTriggers = (userPatterns.triggers || [])
      .sort((a, b) => b.correlation - a.correlation)
      .slice(0, 3);

    const topCoping = (userPatterns.copingMechanisms || [])
      .sort((a, b) => b.effectiveness - a.effectiveness)
      .slice(0, 3);

    patternContext = `

RECURRING THEMES YOU'VE IDENTIFIED:
${topThemes.map(theme =>
  `- "${theme.theme}" (mentioned ${theme.mentionCount} times, ${theme.frequency}, tone: ${theme.emotionalTone}, trend: ${theme.trend})`
).join('\n')}

${topTriggers.length > 0 ? `KNOWN TRIGGERS:
${topTriggers.map(trigger =>
  `- ${trigger.trigger} → ${trigger.emotion} (${Math.round(trigger.correlation * 100)}% correlation${trigger.copingStrategies?.length > 0 ? `, helped by: ${trigger.copingStrategies.join(', ')}` : ''})`
).join('\n')}
` : ''}
${topCoping.length > 0 ? `WHAT HELPS THIS USER:
${topCoping.map(coping =>
  `- ${coping.mechanism} (${Math.round(coping.effectiveness * 100)}% effective for ${coping.situationsUsed?.join(', ') || 'various situations'})`
).join('\n')}
` : ''}
HOW TO USE PATTERNS:
- Reference relevant themes naturally ("This connects to the ${topThemes[0]?.theme || 'themes'} you've been writing about")
- When triggers appear, acknowledge them and suggest proven coping strategies
- If user mentions something similar to past patterns, note growth or changes
- Don't list all patterns - weave them subtly into your response`;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // AI MEMORY CONTEXT (Phase 3)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let memoryContext = '';

  if (userMemory) {
    const preferences = userMemory.feedbackPreferences || {};
    const engagedCount = userMemory.engagedInsights?.length || 0;
    const dismissedCount = userMemory.dismissedInsights?.length || 0;
    const successfulAdvice = userMemory.successfulAdvice || [];

    // Only show if we have enough data
    if (engagedCount + dismissedCount >= 3) {
      memoryContext = `

YOUR COMMUNICATION PREFERENCES (learned from ${engagedCount + dismissedCount} interactions):
- Preferred tone: ${preferences.preferredTone || 'balanced'} (analytical/empathetic/balanced)
- Preferred length: ${preferences.preferredLength || 'detailed'} (concise/detailed)
- Response to questions: ${preferences.likesQuestions ? 'Appreciates reflective questions' : 'Prefers direct insights'}
- Response to challenges: ${preferences.likesChallenges ? 'Responds well to gentle challenges' : 'Prefers supportive feedback'}
${preferences.preferredInsightTypes?.length > 0 ? `- Most engaged insight types: ${preferences.preferredInsightTypes.join(', ')}` : ''}

ENGAGEMENT PATTERN:
- Engaged with ${engagedCount} insights (${Math.round(engagedCount / (engagedCount + dismissedCount) * 100)}% engagement rate)
- Dismissed ${dismissedCount} insights
- This tells you what resonates with this user

${successfulAdvice.length > 0 ? `ADVICE THAT WORKED:
${successfulAdvice.slice(0, 3).map(advice =>
  `- "${advice.advice.substring(0, 80)}..." (${advice.situation} → ${advice.outcome})`
).join('\n')}` : ''}

HOW TO USE THIS:
- Match your tone to their preference (${preferences.preferredTone})
- Adjust length to their preference (${preferences.preferredLength})
- ${preferences.likesQuestions ? 'Ask thoughtful questions' : 'Provide direct insights'}
- Reference successful past advice when relevant
- Learn from dismissed insights (what didn't work)`;
    }
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // SIMILAR PAST ENTRIES (Phase 4.2)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let similarEntriesContext = '';

  if (similarEntries && similarEntries.length > 0) {
    const formattedEntries = formatSimilarEntriesForContext(similarEntries);

    similarEntriesContext = `

SIMILAR PAST ENTRIES (contextual callbacks):
${formattedEntries}

HOW TO USE SIMILAR ENTRIES:
- Reference them naturally: "This reminds me of what you wrote on [date]..."
- Compare and contrast: "Similar to your entry from [date], but I notice you're approaching it differently now"
- Acknowledge growth: "Last time you wrote about this (on [date]), you were feeling [mood]. Now you seem [observation]"
- Connect themes: "This connects to the pattern you've been writing about since [date]"
- Don't list them - weave references organically into your response`;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // BUILD CONTEXT STRING
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // Build user context section based on available data
  let userContextSection = '';

  if (hasProfileData) {
    // Full personalization available
    userContextSection = `
USER CONTEXT (already calculated, use as-is):
- Age: ${age} years old
- Birth Location: ${profile.birthPlace || 'Not specified'}
- Zodiac Sign: ${zodiacSign}
- Current Mood: ${entryContent.mood || 'Not specified'}
- Current Weather: ${entryContent.weather || 'Not specified'}
- Time of Day: ${timeOfDay}
${hexagramContext}${handwritingContext}${historicalContext}${patternContext}${memoryContext}${similarEntriesContext}`;
  } else {
    // Generic context without personalization
    userContextSection = `
USER CONTEXT:
- Current Mood: ${entryContent.mood || 'Not specified'}
- Current Weather: ${entryContent.weather || 'Not specified'}
- Time of Day: ${timeOfDay}${handwritingContext}${historicalContext}

Note: User hasn't provided birth information, so provide general insights without astrological personalization.`;
  }

  const hasHistory = userStats && userStats.totalEntries > 0;
  const wordCount = entryContent.text.trim().split(/\s+/).filter(Boolean).length;
  const isShortEntry = wordCount < 80;

  return `You are this person's clearest inner voice — not a therapist, not an advisor, but the part of them that sees without judgment or agenda. You've just read what they wrote. Now respond.

ENTRY:
"${entryContent.text}"
${userContextSection}

VOICE & TONE:
- Sound like their own thoughts, not outside feedback — calm, grounded, consistent
- Mirror their vocabulary and phrasing back to them naturally when it fits
- Reflective, not prescriptive ("There's something in how you describe this..." not "You should...")
- Never use: "sit with", "hold space", "unpack", "I see you", "lean into", "on your journey", "show up"
- Never mention hexagrams, I Ching, astrology, or Human Design by name
${hasHistory ? '- You know them. Let that show without announcing it.' : '- You\'re just meeting them. Be open and curious rather than making assumptions.'}

${isShortEntry ? `SHORT ENTRY — don't force analysis of what isn't there yet.
Open a conversation instead. Draw on what you know about their recurring themes, interests, and patterns from their writing history. Ask how something specific is going, or introduce a thread worth exploring. It should feel like picking up a conversation, not starting one from scratch.` : `RESPOND DIRECTLY to what they wrote — like you just heard them say it out loud.
1-2 paragraphs. The first responds to what's actually on the page. The second (if needed) goes one layer deeper — a pattern they haven't named, a contradiction, something sitting underneath the surface.
End with one subtle rhetorical question as your final sentence. Make it specific to what they wrote. It should feel like an open door, not an assignment.`}

WHEN TO PUSH BACK (use judgment):
- Self-criticism: don't validate it — reflect it back ("You keep framing this as your fault")
- Writing around something: name what's underneath without forcing it
- Contradicts past writing: note the shift naturally ("This feels different from how you usually talk about this")
- Spiralling or catastrophising: gently interrupt the loop

SENSITIVE CONTENT GUARDRAILS:
If the entry touches on grief, loss, mental health, or something genuinely heavy — soften entirely. No pushback, no probing. Acknowledge what they're carrying. Any closing question should be gentle and open, never pointed.

Return ONLY valid JSON:
{
  "themes": ["2-4 short lowercase labels"],
  "response": "Your full response as one string. ${isShortEntry ? 'A warm conversation opener, 2-3 sentences, ending with a specific question.' : '1-2 paragraphs separated by \\n\\n. Final sentence is a subtle rhetorical question. 150-200 words total.'}"
}`;
}

// Build context for PROMPTS (writing inspiration)
function buildPromptsContext(profile, hexagramProfile, recentThemes, options = {}) {
  const now = new Date();
  const hour = now.getHours();
  const timeOfDay = getTimeOfDay(hour);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // GRACEFUL PROFILE HANDLING WITH RICH DATA
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━
  let hasProfileData = !!(profile && profile.birthDate);
  let hexagramGifts = null;
  let humanDesignStrategy = null;
  let moonSign = null;

  if (hexagramProfile) {
    const personality = hexagramProfile.personalityDetails || hexagramProfile.personality;
    hexagramGifts = personality?.gift || null;

    // Get Human Design strategy for prompts that align with their nature
    if (hexagramProfile.humanDesign) {
      humanDesignStrategy = hexagramProfile.humanDesign.strategy;
    }

    // Get Moon sign for emotional prompts
    if (hexagramProfile.westernAstrology) {
      moonSign = hexagramProfile.westernAstrology.moon;
    }
  }

  // Determine prompt categories based on time of day
  let categories = [];
  let timeGuidance = '';
  if (hour < 12) {
    categories = ['Gratitude', 'Intentions', 'Self-Discovery'];
    timeGuidance = 'Morning is ideal for setting intentions and gratitude';
  } else if (hour < 18) {
    categories = ['Present Moment', 'Processing', 'Self-Discovery'];
    timeGuidance = 'Afternoon is good for checking in with yourself';
  } else {
    categories = ['Reflection', 'Gratitude', 'Processing', 'Dreams'];
    timeGuidance = 'Evening is perfect for reflecting on the day';
  }

  const themesText = recentThemes.length > 0
    ? recentThemes.join(', ')
    : 'self-reflection, growth, awareness';

  // Build rich personalization section
  let personalizationHint = '';
  if (hasProfileData) {
    let hints = [];
    if (hexagramGifts) hints.push(`Natural strengths: ${hexagramGifts}`);
    if (humanDesignStrategy) hints.push(`Life strategy: ${humanDesignStrategy} (create prompts that honor this)`);
    if (moonSign && moonSign !== 'Unknown') hints.push(`Emotional nature: ${moonSign} Moon (consider emotional tone)`);

    if (hints.length > 0) {
      personalizationHint = '\n- ' + hints.join('\n- ');
    }
  }

  const baseContext = `You are a creative journaling companion. Generate${hasProfileData ? ' deeply personalized' : ''} writing prompts.

USER CONTEXT:
- Recent themes: ${themesText}
- Time of Day: ${timeOfDay} (${timeGuidance})${personalizationHint}

YOUR TASK:
Generate 4-5${hasProfileData ? ' personalized' : ''} writing prompts that:
1. Help overcome writer's block
2. Are relevant to their recent themes${hasProfileData ? ' and personal strengths' : ''}
3. Are appropriate for ${timeOfDay}
4. Range from light (gratitude) to deep (self-reflection)
5. Feel natural, inviting, open-ended

Return ONLY a valid JSON array (no markdown, no code blocks) with this exact structure:
[
  {
    "category": "${categories[0]}",
    "prompt": "Open-ended question starting with What/How/When/If..."
  },
  {
    "category": "${categories[1] || 'Self-Discovery'}",
    "prompt": "Another thoughtful prompt..."
  },
  {
    "category": "${categories[2] || 'Processing'}",
    "prompt": "Another thoughtful prompt..."
  },
  {
    "category": "Present Moment",
    "prompt": "Another thoughtful prompt..."
  }
]

PROMPT GUIDELINES:
- Start with "What...", "How...", "When...", "If...", or "Where..."
- Be specific but open-ended
- Encourage deeper self-discovery
- Keep warm, gentle, inviting tone
- Avoid clichés and generic prompts
${hasProfileData ? '- Make them feel personally relevant to their strengths' : '- Focus on universal human experiences'}
- Return ONLY valid JSON array, no extra text`;

  // Append AI-connected writing context sections
  const contextParts = [baseContext];

  if (options.signalFocus) {
    contextParts.push(`\nRECURRING PATTERN FOCUS:\nThe user wants to explore a recurring pattern: "${options.signalFocus.label}" (${options.signalFocus.polarity || 'neutral'} energy, confidence: ${Math.round((options.signalFocus.score || 0) * 100)}%). Shape 2 of the 4 prompts around going deeper into this specific theme. Use the framing: "I've noticed [X] showing up — what's underneath that?"`);
  }

  if (options.entryStage === 'orientation') {
    contextParts.push(`\nSTAGE GUIDANCE:\nThis user is early in their journaling journey. Keep all prompts brief, welcoming, and low-pressure. No complexity.`);
  } else if (options.entryStage === 'personal_os') {
    contextParts.push(`\nSTAGE GUIDANCE:\nThis user has an extensive journaling history. Skip preamble. One sharp, precise question per prompt. No warm-up.`);
  }

  if (options.feedbackPreferences?.prefersActionable) {
    contextParts.push(`\nFEEDBACK STYLE:\nThis user engages most with actionable content. End at least 2 prompts with a specific micro-action or concrete next step.`);
  } else if (options.feedbackPreferences?.prefersReflective) {
    contextParts.push(`\nFEEDBACK STYLE:\nThis user prefers open reflection. Keep prompts as open questions — avoid directives or action items.`);
  }

  if (options.transitHints?.length > 0) {
    contextParts.push(`\nATMOSPHERIC CONTEXT (optional lens — frame one prompt around this, never assert it's affecting the user):\n${options.transitHints[0]}`);
  }

  return contextParts.join('');
}

// Parse AI analysis response
function parseAnalysisResponse(aiResponse) {
  try {
    let jsonText = aiResponse.feedback || aiResponse.text || aiResponse;

    // Remove markdown code blocks if present
    jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

    const parsed = JSON.parse(jsonText);

    return {
      themes: parsed.themes || [],
      response: parsed.response || ''
    };
  } catch (err) {
    logger.error('Failed to parse analysis response:', err);
    logger.debug('Raw response:', aiResponse);

    // Fallback: surface raw text as the response
    const rawText = aiResponse.feedback || aiResponse.text || String(aiResponse);
    return {
      themes: [],
      response: rawText
    };
  }
}

// Parse AI prompts response
function parsePromptsResponse(aiResponse) {
  try {
    // Try to extract JSON from response
    let jsonText = aiResponse.feedback || aiResponse.text || aiResponse;

    // Remove markdown code blocks if present
    jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

    const parsed = JSON.parse(jsonText);
    const promptsArray = Array.isArray(parsed) ? parsed : (parsed.prompts || []);

    return {
      context: 'Based on your recent reflections and current energy',
      prompts: promptsArray
    };
  } catch (err) {
    logger.error('Failed to parse prompts response:', err);
    logger.debug('Raw response:', aiResponse);

    // Fallback prompts
    return {
      context: 'Here are some prompts to inspire your writing',
      prompts: [
        { category: 'Self-Discovery', prompt: 'What are you learning about yourself right now?' },
        { category: 'Gratitude', prompt: 'What small thing brought you joy today?' },
        { category: 'Processing', prompt: 'What emotion needs your attention today?' }
      ]
    };
  }
}

// Extract themes from recent entries
function extractRecentThemes(entries) {
  if (!entries || entries.length === 0) return [];

  const themes = new Set();
  entries.slice(0, 10).forEach(entry => {
    // Look for AI insights themes
    if (entry.aiInsights?.themes) {
      entry.aiInsights.themes.forEach(theme => themes.add(theme));
    }
    // Also check mood as a theme
    if (entry.mood) {
      themes.add(entry.mood);
    }
  });

  return Array.from(themes).slice(0, 5);
}

// Determine time of day from hour
function getTimeOfDay(hour) {
  if (hour < 6) return 'late night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'night';
}

// Calculate zodiac sign from birthdate
// Export for use in other files (ProfileContext, astronomical calculators, etc.)
export function calculateZodiacSign(birthDate) {
  const month = birthDate.getMonth();
  const day = birthDate.getDate();

  if ((month === 0 && day <= 19) || (month === 11 && day >= 22)) return 'Capricorn';
  if ((month === 0 && day >= 20) || (month === 1 && day <= 18)) return 'Aquarius';
  if ((month === 1 && day >= 19) || (month === 2 && day <= 20)) return 'Pisces';
  if ((month === 2 && day >= 21) || (month === 3 && day <= 19)) return 'Aries';
  if ((month === 3 && day >= 20) || (month === 4 && day <= 20)) return 'Taurus';
  if ((month === 4 && day >= 21) || (month === 5 && day <= 20)) return 'Gemini';
  if ((month === 5 && day >= 21) || (month === 6 && day <= 22)) return 'Cancer';
  if ((month === 6 && day >= 23) || (month === 7 && day <= 22)) return 'Leo';
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Virgo';
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Libra';
  if ((month === 9 && day >= 23) || (month === 10 && day <= 21)) return 'Scorpio';
  return 'Sagittarius';
}